import { Suspense } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ProductsPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="bg-black min-h-screen">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center">
              <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-6">
                Products & Solutions
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                Comprehensive security systems tailored to protect what matters most
              </p>
            </div>
          </div>
        </section>

        {/* Access Control Systems */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-block px-4 py-2 bg-coral-50 rounded-full mb-6">
                  <span className="text-coral-600 font-semibold text-sm">ADVANCED SECURITY</span>
                </div>
                <h2 className="font-serif text-4xl font-bold text-slate-900 mb-6">
                  Access Control Systems
                </h2>
                <p className="text-lg text-slate-600 mb-6">
                  Manage and monitor who enters your property with state-of-the-art access control solutions. Our systems provide seamless integration with your existing infrastructure.
                </p>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-coral-600 mr-3 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Keycard & Biometric Entry</h3>
                      <p className="text-slate-600">Multi-factor authentication for enhanced security</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-coral-600 mr-3 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Remote Management</h3>
                      <p className="text-slate-600">Control access from anywhere via mobile app</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-coral-600 mr-3 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Audit Trail Logging</h3>
                      <p className="text-slate-600">Complete records of all entry and exit events</p>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/bfba021bc2b2f3d208f4747d3a9cccc3.jpg"
                  alt="Access Control System"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Surveillance IP Systems */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1 relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=High-resolution%20IP%20security%20camera%20system%20installation%20on%20commercial%20building%20exterior%2C%20multiple%20dome%20and%20bullet%20cameras%2C%20professional%20mounting%2C%20network%20cables%2C%20modern%20surveillance%20equipment%2C%20clear%20blue%20sky%20background%2C%20professional%20photography%2C%20realistic%20detailed%20style&width=600&height=800&seq=products-surveillance-1&orientation=portrait"
                  alt="Surveillance IP System"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="order-1 md:order-2">
                <div className="inline-block px-4 py-2 bg-coral-50 rounded-full mb-6">
                  <span className="text-coral-600 font-semibold text-sm">24/7 MONITORING</span>
                </div>
                <h2 className="font-serif text-4xl font-bold text-slate-900 mb-6">
                  Surveillance IP Systems
                </h2>
                <p className="text-lg text-slate-600 mb-6">
                  Crystal-clear video surveillance with intelligent analytics. Our IP camera systems deliver unmatched image quality and smart detection capabilities.
                </p>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-coral-600 mr-3 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">4K Ultra HD Resolution</h3>
                      <p className="text-slate-600">Capture every detail with exceptional clarity</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-coral-600 mr-3 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">AI-Powered Analytics</h3>
                      <p className="text-slate-600">Smart motion detection and facial recognition</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-coral-600 mr-3 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Cloud Storage Integration</h3>
                      <p className="text-slate-600">Secure backup and remote access to footage</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Intercom Systems */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-block px-4 py-2 bg-coral-50 rounded-full mb-6">
                  <span className="text-coral-600 font-semibold text-sm">SMART COMMUNICATION</span>
                </div>
                <h2 className="font-serif text-4xl font-bold text-slate-900 mb-6">
                  Intercom Systems
                </h2>
                <p className="text-lg text-slate-600 mb-6">
                  Modern video intercom solutions that connect you with visitors before granting access. Perfect for residential buildings, offices, and gated communities.
                </p>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-coral-600 mr-3 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">HD Video & Audio</h3>
                      <p className="text-slate-600">See and speak with visitors in high definition</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-coral-600 mr-3 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Mobile Integration</h3>
                      <p className="text-slate-600">Answer calls from anywhere using your smartphone</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-coral-600 mr-3 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Multi-Unit Support</h3>
                      <p className="text-slate-600">Scalable solutions for buildings of any size</p>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20video%20intercom%20system%20installed%20at%20building%20entrance%2C%20touchscreen%20display%20with%20camera%2C%20sleek%20black%20design%2C%20professional%20installation%2C%20LED%20ring%20light%2C%20contemporary%20architecture%20background%2C%20daytime%20shot%2C%20realistic%20photography%20style%2C%20high%20quality%20details&width=600&height=800&seq=products-intercom-1&orientation=portrait"
                  alt="Intercom System"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Intrusion Detection Systems */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1 relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20intrusion%20detection%20system%20control%20panel%20with%20motion%20sensors%20and%20door%20contacts%2C%20white%20security%20panel%20with%20LED%20indicators%2C%20modern%20alarm%20system%2C%20professional%20installation%20on%20wall%2C%20clean%20wiring%2C%20residential%20interior%20background%2C%20realistic%20photography%2C%20high%20detail%20quality&width=600&height=800&seq=products-intrusion-1&orientation=portrait"
                  alt="Intrusion Detection System"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="order-1 md:order-2">
                <div className="inline-block px-4 py-2 bg-coral-50 rounded-full mb-6">
                  <span className="text-coral-600 font-semibold text-sm">INSTANT ALERTS</span>
                </div>
                <h2 className="font-serif text-4xl font-bold text-slate-900 mb-6">
                  Intrusion Detection Systems
                </h2>
                <p className="text-lg text-slate-600 mb-6">
                  Advanced alarm systems that detect unauthorized entry and alert you instantly. Protect your property with multi-layered security detection.
                </p>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-coral-600 mr-3 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Perimeter Protection</h3>
                      <p className="text-slate-600">Door, window, and glass break sensors</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-coral-600 mr-3 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Motion Detection</h3>
                      <p className="text-slate-600">Advanced PIR sensors with pet immunity</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-2xl text-coral-600 mr-3 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">24/7 Monitoring</h3>
                      <p className="text-slate-600">Professional monitoring service available</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-coral-600 to-coral-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Secure Your Property?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Let our experts design a custom security solution for your needs
            </p>
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/contact')}
              className="inline-flex items-center px-8 py-4 bg-white text-coral-600 rounded-full font-semibold text-lg hover:bg-slate-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Schedule Consultation
              <i className="ri-arrow-right-line ml-2 text-xl"></i>
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}
